import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest, HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Event, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from "@angular/router";
import { Attributes, AttributeValue, Context, context, propagation, ROOT_CONTEXT, Span, SpanContext, SpanKind, SpanStatusCode, trace, Tracer } from "@opentelemetry/api";
import { logs, SeverityNumber } from "@opentelemetry/api-logs";
import {
  ATTR_ERROR_TYPE,
  ATTR_HTTP_REQUEST_METHOD,
  ATTR_HTTP_RESPONSE_STATUS_CODE,
  ATTR_HTTP_ROUTE,
  ATTR_SERVER_ADDRESS,
  ATTR_URL_FULL,
  ATTR_URL_PATH,
} from "@opentelemetry/semantic-conventions";
import { ATTR_SESSION_ID } from "@opentelemetry/semantic-conventions/incubating";
import { environment } from "environments/environment";
import { List, Map } from "immutable";
import { filter, Observable, Subscriber, Subscription, TeardownLogic } from "rxjs";
import { httpRoute } from "./http-route";
import { navigationContext, setNavigationSpanContext } from "./navigation-context";
import { replaySessionId } from "./replay-session";
import { telemetryUserAttributes } from "./telemetry-user";

type NavigationBoundary = NavigationStart | NavigationEnd | NavigationCancel | NavigationError;

// TypeScript's DOM lib has no Long Animation Frames types yet.
interface LongAnimationFrameScript {
  invoker: string;
  invokerType: string;
  sourceURL: string;
  sourceFunctionName: string;
  startTime: number;
  duration: number;
}

interface LongAnimationFrameTiming extends PerformanceEntry {
  blockingDuration: number;
  renderStart: number;
  styleAndLayoutStart: number;
  scripts: LongAnimationFrameScript[];
}

@Injectable({ providedIn: "root" })
export class TelemetryService {
  private _router = inject(Router);
  private readonly _tracer: Tracer = trace.getTracer("lims-router");
  private _navigation: Span | null = null;
  // The last navigation that completed: a cancelled or failed one leaves the user on this page.
  private _settledSpanContext: SpanContext | null = null;
  private _subscription: Subscription | null = null;

  /** Reports a failed request under its own span; network failures and 5xx as errors, other statuses as warnings. */
  private _reportHttpError(error: HttpErrorResponse, span: Span, method: string, url: string): void {
    const route: string = httpRoute(new URL(url, location.origin).pathname);
    // Read synchronously: the problem body resolves later, after the page or user may have changed.
    const attributes: Map<string, AttributeValue | undefined> = Map<string, AttributeValue | undefined>({
      [ATTR_HTTP_REQUEST_METHOD]: method,
      [ATTR_HTTP_RESPONSE_STATUS_CODE]: error.status,
      [ATTR_HTTP_ROUTE]: route,
      [ATTR_ERROR_TYPE]: error.status === 0 ? "network" : String(error.status),
      [ATTR_URL_PATH]: location.pathname,
      [ATTR_SESSION_ID]: replaySessionId() ?? undefined,
      ...telemetryUserAttributes(),
    });
    const severity: SeverityNumber = error.status === 0 || error.status >= 500 ? SeverityNumber.ERROR : SeverityNumber.WARN;

    // NSwag clients receive errors as a Blob; HttpClient callers asking for JSON receive a plain parsed object, and network failures a ProgressEvent.
    void (
      error.error instanceof Blob
        ? error.error.type === "application/problem+json"
          ? error.error.text().then((text: string): object => JSON.parse(text) ?? {})
          : Promise.resolve({})
        : Promise.resolve(error.error?.constructor === Object ? error.error : {})
    )
      .catch((): object => ({}))
      .then(({ title, type, traceId }: { title?: unknown; type?: unknown; traceId?: unknown }): void => {
        try {
          logs.getLogger("lims-http-errors").emit({
            severityNumber: severity,
            severityText: SeverityNumber[severity],
            body: `${method} ${route} failed with ${error.status}`,
            attributes: attributes
              .merge(
                Map<string, unknown>({ "problem.title": title, "problem.type": type, "problem.trace_id": traceId }).filter(
                  (value: unknown): value is string => typeof value === "string"
                )
              )
              .filter((value: AttributeValue | undefined): boolean => value !== undefined && value !== "")
              .toObject() as Attributes,
            context: trace.setSpan(ROOT_CONTEXT, span),
          });
        } catch {}
      });
  }

  /** Traces a request as a CLIENT span and delivers its events with that span active. */
  traceRequest(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    return new Observable<HttpEvent<unknown>>((subscriber: Subscriber<HttpEvent<unknown>>): TeardownLogic => {
      let span: Span;
      let active: Context;
      let traced: HttpRequest<unknown>;
      try {
        const parent: Context = context.active();
        const url: URL = new URL(req.url, location.origin);
        // Started in the root zone so span bookkeeping never schedules a task that triggers change detection.
        span = Zone.root.run((): Span =>
          trace.getTracer("lims-http").startSpan(
            `${req.method} ${httpRoute(url.pathname)}`,
            {
              kind: SpanKind.CLIENT,
              attributes: { [ATTR_HTTP_REQUEST_METHOD]: req.method, [ATTR_URL_FULL]: url.href, [ATTR_URL_PATH]: url.pathname, [ATTR_SERVER_ADDRESS]: url.hostname },
            },
            parent
          )
        );
        active = trace.setSpan(parent, span);
        const headers: { [name: string]: string } = {};
        // Only the API's CORS policy allows traceparent; third-party preflights reject it.
        if (
          List([localStorage.getItem("serverLocationUrl"), environment.baseURL]).some(
            (base: string | null): boolean => !!base && new URL(base, location.origin).origin === url.origin
          )
        ) {
          propagation.inject(active, headers);
        }
        traced = req.clone({ setHeaders: headers });
      } catch {
        return next(req).subscribe(subscriber);
      }

      const subscription: Subscription = next(traced).subscribe({
        next: (event: HttpEvent<unknown>): void => {
          if (event instanceof HttpResponse) {
            this._quietly((): unknown => span.setAttribute(ATTR_HTTP_RESPONSE_STATUS_CODE, event.status));
          }
          context.with(active, (): void => subscriber.next(event));
        },
        error: (error: unknown): void => {
          this._quietly((): void => {
            const status: number | undefined = error instanceof HttpErrorResponse ? error.status : undefined;
            if (status !== undefined) {
              span.setAttribute(ATTR_HTTP_RESPONSE_STATUS_CODE, status);
            }
            span.setAttribute(ATTR_ERROR_TYPE, status === 0 ? "network" : status !== undefined && status >= 400 ? String(status) : ((error as Error | null)?.name ?? "_OTHER"));
            span.setStatus({ code: SpanStatusCode.ERROR });
            // 401s are the expected token-expiry path.
            if (error instanceof HttpErrorResponse && error.status !== 401) {
              this._reportHttpError(error, span, req.method, req.url);
            }
          });
          context.with(active, (): void => subscriber.error(error));
        },
        complete: (): void => subscriber.complete(),
      });

      // Runs once on completion, error or unsubscribe, so a cancelled request still ends its span.
      return (): void => {
        subscription.unsubscribe();
        this._quietly((): void => Zone.root.run((): void => span.end()));
      };
    });
  }

  /** Records frames that blocked the main thread for 100 ms or more, with the scripts that ran, under the current navigation. */
  trackLongAnimationFrames(): Subscription {
    // Chromium only; other browsers record nothing until they ship the API.
    if (!PerformanceObserver.supportedEntryTypes?.includes("long-animation-frame")) {
      return Subscription.EMPTY;
    }

    const observer: PerformanceObserver = new PerformanceObserver((list: PerformanceObserverEntryList): void => {
      try {
        List(list.getEntries() as LongAnimationFrameTiming[])
          .filter((frame: LongAnimationFrameTiming): boolean => frame.duration >= 100)
          .forEach((frame: LongAnimationFrameTiming): void => {
            const span: Span = trace.getTracer("lims-main-thread").startSpan(
              "long-animation-frame",
              {
                startTime: frame.startTime,
                attributes: {
                  "long_animation_frame.duration": frame.duration,
                  "long_animation_frame.blocking_duration": frame.blockingDuration,
                  "long_animation_frame.render_start": frame.renderStart,
                  "long_animation_frame.style_and_layout_start": frame.styleAndLayoutStart,
                },
              },
              navigationContext()
            );
            frame.scripts.forEach((script: LongAnimationFrameScript): void => {
              span.addEvent(
                "script",
                {
                  "script.invoker": script.invoker,
                  "script.invoker_type": script.invokerType,
                  "script.source_url": script.sourceURL,
                  "script.source_function_name": script.sourceFunctionName,
                  "script.duration": script.duration,
                },
                script.startTime
              );
            });
            span.end(frame.startTime + frame.duration);
          });
      } catch {}
    });
    observer.observe({ type: "long-animation-frame", buffered: true });

    return new Subscription((): void => observer.disconnect());
  }

  trackRouteChanges(): Subscription {
    // unsubscribe() does not clear this field, so resubscribe after a teardown.
    if (this._subscription && !this._subscription.closed) {
      return this._subscription;
    }

    this._subscription = this._router.events
      .pipe(
        filter((event: Event): event is NavigationBoundary =>
          List([NavigationStart, NavigationEnd, NavigationCancel, NavigationError]).some((boundary): boolean => event instanceof boundary)
        )
      )
      .subscribe((event: NavigationBoundary): void => {
        if (event instanceof NavigationStart) {
          if (this._navigation) {
            this._navigation.setAttribute("router.superseded", true);
            this._navigation.end();
          }
          // ROOT_CONTEXT keeps every navigation a trace root rather than a child of whichever HTTP span triggered it.
          this._navigation = this._tracer.startSpan("router.navigate", undefined, ROOT_CONTEXT);
          this._navigation.setAttribute("router.url", event.url);
          setNavigationSpanContext(this._navigation.spanContext());
          return;
        }

        if (!this._navigation) {
          return;
        }

        if (event instanceof NavigationEnd) {
          this._navigation.setAttribute("router.url_after_redirects", event.urlAfterRedirects);
          this._settledSpanContext = this._navigation.spanContext();
        } else if (event instanceof NavigationError) {
          setNavigationSpanContext(this._settledSpanContext);
          this._navigation.recordException(event.error);
          this._navigation.setStatus({ code: SpanStatusCode.ERROR, message: event.error?.message ?? String(event.error) });
        } else if (event instanceof NavigationCancel) {
          setNavigationSpanContext(this._settledSpanContext);
          this._navigation.setAttribute("router.cancellation_code", event.code);
        }

        this._navigation.end();
        this._navigation = null;
      });

    return this._subscription;
  }

  /** Runs span work that must never change what an HTTP caller receives. */
  private _quietly(work: () => unknown): void {
    try {
      work();
    } catch {}
  }
}
